import React, { useState } from 'react'
import type { Entry } from 'contentful'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'

import { buttonStyles, type VariantType } from '@/components/ui/cta-link'

import { cx } from '@/utils/strings'
import useMutationObserver from '@/utils/useMutationObserver'
import type { TypeComponentCtaLinkSkeleton, TypeComponentHubspotCtaSkeleton, TypeSectionCtaSkeleton } from '@/types/ctf'
import { isTypeComponentLottie, isTypeResourceImage } from '@/types/ctf'
import { useEventListener } from 'usehooks-ts'

import Lottie from '../lottie'
import gradients from '@/constants/gradients'
import Image from '@/components/ui/image'

import { getBackground } from '@/utils/backgrounds'
import { useInspectorProps } from '@/utils/live-preview'

type SectionCtaEntry = Entry<TypeSectionCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ComponentCtaLinkEntry = Entry<TypeComponentCtaLinkSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ComponentHubspotCtaEntry = Entry<TypeComponentHubspotCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SectionCtaProps = SectionCtaEntry['fields'] & {
  entryId?: string
  className?: string
}

export default function Cta({
  entryId,
  actions,
  backgroundColor: _backgroundColor,
  backgroundImage,
  backgroundImageOverlay,
  body,
  className,
  eyebrowText,
  headline,
  image,
  imageAlignment = 'Center',
  imageScale = 'Medium',
  sticky,
  textAlign = 'Left',
  textImageAlignment = 'Image Right',
  asset,
  largeText,
  size = 'Regular',
}: SectionCtaProps) {
  const pageInspectorProps = useInspectorProps({ entryId, restrictedTypes: ['pages'] })

  const backgroundColor = _backgroundColor || gradients.midnight
  const [headerHeight, setHeaderHeight] = useState(0)
  const calcHeaderHeight = () => {
    const header = document.querySelector('header')
    if (header) {
      const height = header.getBoundingClientRect().bottom
      setHeaderHeight(height)
    }
  }
  useMutationObserver('header', calcHeaderHeight)
  useEventListener('resize', calcHeaderHeight)

  const renderCTA = (
    action: ComponentCtaLinkEntry | ComponentHubspotCtaEntry | undefined,
    btnStyle: VariantType,
    scrollToOffset = 0
  ) => {
    if (!action) return null
    const type = action.sys.contentType.sys.id
    const fields = {
      type,
      ...action.fields,
    } as DynamicActionProps
    return (
      <DynamicAction
        key={action.sys.id}
        {...fields}
        style={btnStyle}
        scrollToOffset={scrollToOffset}
        id={action.sys.id}
      />
    )
  }

  // Content wrapper — always uses a div so content stays above the absolute background image
  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    return (
      <div
        className={cx(
          'relative z-10 p-6',
          !hasVisualAsset || textImageAlignment === 'Image Center' ? 'w-full' : 'md:w-1/2',
          size === 'Regular' && 'lg:px-10 md:py-10',
          size === 'Medium' && 'lg:px-10 md:py-30',
          size === 'Large' && 'lg:px-10 md:py-45'
        )}
      >
        {children}
      </div>
    )
  }

  const hasVisualAsset = image || asset
  // URL used for the "Large" cover fill (bg-cover layer). Lottie assets have no
  // file URL, so they fall back to the normal <Image> render.
  const coverImageUrl =
    image?.fields?.file?.url ??
    (asset && isTypeResourceImage(asset) ? asset.fields.image?.fields?.file?.url : undefined)
  const contentLayoutClasses = cx(
    hasVisualAsset && 'flex gap-2 md:gap-8 md:flex-row items-center',
    hasVisualAsset && imageAlignment !== 'Bottom' && 'flex-col md:flex-col-reverse',
    hasVisualAsset && imageAlignment === 'Bottom' && 'flex-col',
    textImageAlignment === 'Image Left' && 'md:flex-row-reverse',
    (textAlign === 'Center' || !textAlign) && 'text-center',
    textAlign === 'Left' && 'text-left',
    textAlign === 'Right' && 'text-right'
  )
  const contentContainerClasses = cx(contentLayoutClasses, 'rounded-lg ')

  const renderImages = () => {
    return (
      <>
        {image && <Image image={image} className="rounded" />}
        {asset && !image && (
          <>
            {isTypeComponentLottie(asset) && (
              <Lottie {...asset.fields} metadata={(asset.fields.metadata as any) ?? undefined} />
            )}
            {isTypeResourceImage(asset) && <Image {...asset.fields} />}
          </>
        )}
      </>
    )
  }
  return sticky ? (
    <section
      id="sticky-cta"
      className={cx('sm:my-10 max-w-8xl mx-auto sm:sticky z-20 px-5 sm:px-9 sm:w-fit', className)}
      style={headerHeight ? { top: `${headerHeight + 12}px` } : undefined}
      {...pageInspectorProps({ fieldId: 'headline' })}
    >
      {actions && (
        <div
          className={cx(
            'flex flex-col shrink-0 flex-wrap gap-0 sm:gap-4 sm:flex-row rounded sm:rounded-full p-6 sm:p-3 sm:shadow-even-around bg-white dark:bg-secondary-800',
            (textAlign === 'Center' || !textAlign) && 'items-center',
            textAlign === 'Left' && 'items-start',
            textAlign === 'Right' && 'items-end'
          )}
          data-testid="actions"
        >
          {actions.map((action: any, idx) => (
            <div key={idx}>
              <div className="block sm:hidden">{renderCTA(action, buttonStyles.btnArrow)}</div>
              <div className={cx(sticky ? `hidden sm:flex` : `flex`, `items-center relative`)}>
                {renderCTA(action, buttonStyles.btnSolid, 55)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  ) : (
    <section
      className={cx('max-w-8xl mx-auto px-5 lg:px-9', className)}
      {...pageInspectorProps({ fieldId: 'headline' })}
    >
      <div className={cx(backgroundColor && getBackground(backgroundColor), 'relative', 'rounded-lg overflow-hidden')}>
        {(body || eyebrowText || headline || actions) && (
          <div className={contentContainerClasses}>
            <ContentWrapper>
              {eyebrowText && <p className="text-eyebrow uppercase mb-3">{eyebrowText}</p>}
              {headline && <h4 className={cx(largeText ? 'h2' : 'h4', 'text-balance')}>{headline}</h4>}
              {body && (
                <div className={cx(largeText && '[&_p]:text-2xl', '[&_p]:[text-wrap:balance]  mb-6')}>
                  <RichText body={body} />
                </div>
              )}
              {textImageAlignment === 'Image Center' && <div className="max-w-4xl mx-auto">{renderImages()}</div>}
              {actions && (
                <div
                  className={cx(
                    !body && headline && 'mt-6',
                    !body && !headline && 'mt-0',
                    'flex shrink-0 flex-wrap gap-5 flex-row w-full items-center',
                    textAlign === 'Center' && 'items-center justify-center'
                  )}
                  data-testid="actions"
                >
                  {actions.map((action: any) => {
                    const btnstyle =
                      backgroundColor === 'Primary Blue' || backgroundColor === gradients.midnight
                        ? buttonStyles.btnOutlinedTertiary
                        : buttonStyles.btnSolid
                    return (
                      <div key={action.sys.id} className="w-full sm:w-auto">
                        <div className="items-center relative dark:after:bg-secondary-500">
                          {renderCTA(action, btnstyle, 50)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </ContentWrapper>
            {hasVisualAsset && textImageAlignment && textImageAlignment !== 'Image Center' && (
              <div
                className={cx(
                  'relative z-10 overflow-hidden md:w-1/2 md:self-stretch',

                  // Large renders as an absolute bg layer with no intrinsic size. On mobile
                  // the cell would collapse (no width in an items-center column, no height),
                  // so give it full width + a padding-top aspect box for height. At md the
                  // row's self-stretch supplies the height, so drop the padding.
                  imageScale === 'Large' && 'w-full pt-[56.25%] md:pt-0'
                )}
              >
                {imageScale === 'Large' && coverImageUrl ? (
                  // Fill the cell like object-fit: cover, using a background layer
                  // (same approach as backgroundImage) rather than fighting the
                  // <Image> figure's intrinsic aspect-ratio / max-width.
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${coverImageUrl})` }}
                  />
                ) : (
                  <div
                    className={cx(
                      'md:absolute [&_svg]:fill-none [&_figure]:m-0 [&_svg]:w-full [&_svg]:h-auto ',
                      imageAlignment === 'Left' &&
                        'md:left-0 md:top-1/2 md:-translate-y-1/2 px-5 py-5 md:px-0 md:py-10',
                      imageAlignment === 'Top' && 'md:top-0 md:left-1/2 md:-translate-x-1/2 [&_figure]:mx-auto',
                      imageAlignment === 'Bottom' &&
                        'md:bottom-0 md:left-1/2 md:-translate-x-1/2 [&_figure]:mx-auto px-5 md:px-0',
                      imageAlignment === 'Right' &&
                        'md:right-0 md:top-1/2 md:-translate-y-1/2 px-5 py-5 md:px-0 md:py-10',
                      imageAlignment === 'Center' &&
                        'md:top-1/2 md:-translate-y-1/2 md:left-1/2 md:-translate-x-1/2 [&_figure]:mx-auto px-5 py-5 md:px-0 md:py-10',
                      imageScale === 'Medium' && 'mx-auto md:mx-0 w-[90%] md:w-[80%]',
                      imageScale === 'Small' && 'mx-auto md:mx-0 w-[80%] md:w-[60%]'
                      // imageScale === 'Small' &&
                      //   (imageAlignment === 'Left' || imageAlignment === 'Right' || imageAlignment === 'Center') &&
                      //   'w-[60%] px-30!'
                    )}
                  >
                    {renderImages()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {backgroundImage && (
          <div
            className={cx(
              'rounded-lg absolute left-0 right-0 top-0 bottom-0 w-full h-full z-0 bg-cover bg-center bg-no-repeat',
              // backgroundImageOverlay drops a black scrim over the image for contrast.
              // When the layout is side-by-side, fade the scrim across the image so the
              // dark end (70%) sits behind the text and the light end (55%) is over the
              // image: text on the left (Image Right) → darker on the left, and vice versa.
              backgroundImageOverlay && "after:content-[''] after:absolute after:inset-0 after:rounded-lg",
              backgroundImageOverlay &&
                textImageAlignment === 'Image Right' &&
                'after:[background-image:linear-gradient(to_right,rgba(0,0,0,0.65),rgba(0,0,0,0.5))]',
              backgroundImageOverlay &&
                textImageAlignment === 'Image Left' &&
                'after:[background-image:linear-gradient(to_left,rgba(0,0,0,0.65),rgba(0,0,0,0.5))]',
              backgroundImageOverlay &&
                (!textImageAlignment || textImageAlignment === 'Image Center') &&
                'after:bg-black/55'
            )}
            style={backgroundImage ? { backgroundImage: `url(${backgroundImage.fields.file?.url})` } : undefined}
          />
        )}
      </div>
    </section>
  )
}
